import { useState, useCallback } from "react";
import { pathwayExplorerClient } from "../services/PathwayExplorerClient";
import { OneHopNeighborEdge } from "../services/PathwayExplorerClient";
import { getApiErrorMessage } from "../utils/apiErrorUtils";

export interface NetworkNode {
  id: string;
  label: string;
}

export interface NetworkEdge {
  source: string;
  target: string;
  path: string[];
  path_length: number;
}

export interface NetworkData {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
}

export const useNetworkData = () => {
  const [networkData, setNetworkData] = useState<NetworkData | null>(null);
  const [isPlottingNetwork, setIsPlottingNetwork] = useState(false);
  const [plotError, setPlotError] = useState<string | null>(null);

  const buildNetworkData = (data: OneHopNeighborEdge[]): NetworkData => {
    const nodeSet = new Set<string>();

    const normalized = data.map((edge) => {
      const path = Array.isArray(edge.path) ? edge.path : [];
      const pathLen = Number(edge.path_length);
      const source =
        (edge.source != null && String(edge.source).trim()) ||
        (path.length > 0 ? path[0] : "");
      const target =
        (edge.target != null && String(edge.target).trim()) ||
        (path.length > 0 ? path[path.length - 1] : "");
      return {
        source: source.trim(),
        target: target.trim(),
        path,
        path_length: pathLen,
      };
    });

    normalized.forEach((edge) => {
      if (edge.source) nodeSet.add(edge.source);
      if (edge.target) nodeSet.add(edge.target);
    });

    const nodes: NetworkNode[] = Array.from(nodeSet).map((gene) => ({
      id: gene,
      label: gene,
    }));

    const edges: NetworkEdge[] = normalized
      .filter((edge) => {
        if (!edge.source || !edge.target) return false;
        if (edge.source === edge.target) return false;
        if (Number(edge.path_length) === 0) return false;
        return true;
      })
      .map((edge) => ({
        source: edge.source,
        target: edge.target,
        path: edge.path,
        path_length: edge.path_length,
      }));

    return { nodes, edges };
  };

  const plotNetwork = useCallback(
    async (
      edgetype: string,
      pathway_genes: string[],
      edge_threshold: number,
    ): Promise<NetworkData | null> => {
      setIsPlottingNetwork(true);
      setPlotError(null);
      try {
        const response = await pathwayExplorerClient.getOneHopNeighbors({
          edgetype,
          pathway_genes,
          edge_threshold,
        });

        if (Array.isArray(response) && response.length > 0) {
          const data = buildNetworkData(response);
          setNetworkData(data);
          return data;
        }
        setNetworkData(null);
        return null;
      } catch (error) {
        console.error("Error plotting network:", error);
        setNetworkData(null);
        setPlotError(getApiErrorMessage(error));
        return null;
      } finally {
        setIsPlottingNetwork(false);
      }
    },
    []
  );

  return {
    networkData,
    isPlottingNetwork,
    plotError,
    setPlotError,
    plotNetwork,
    setNetworkData,
  };
};
